import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import { useParams } from "react-router-dom";
import { integrationsApi } from "../../api/integrations";
import { fiscalApi } from "../../api/fiscal";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import { useProjectSettingsDirty } from "../../components/dashboard/ProjectSettingsUnsavedContext";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };
const ISSUER_CONDITIONS = [
  { value: 1, label: "IVA Responsable Inscripto" },
  { value: 6, label: "Responsable Monotributo" },
  { value: 4, label: "IVA Exento" },
];
const RECIPIENT_CONDITIONS = [
  { value: 5, label: "Consumidor Final" },
  { value: 1, label: "IVA Responsable Inscripto" },
  { value: 6, label: "Responsable Monotributo" },
  { value: 4, label: "IVA Exento" },
  { value: 7, label: "Sujeto No Categorizado" },
  { value: 10, label: "IVA Liberado" },
  { value: 13, label: "Monotributista Social" },
  { value: 15, label: "IVA No Alcanzado" },
  { value: 16, label: "Monotributo Trabajador Independiente Promovido" },
];
const VOUCHER_LABELS = { 1: "Factura A", 6: "Factura B", 11: "Factura C" };
const A_RECIPIENT_CODES = new Set([1, 6, 13, 16]);

const emptyIntegration = {
  enabled: true,
  connected: false,
  connectionStatus: "not_configured",
  environment: "prod",
  cuit: "",
  legalName: "",
  pointOfSale: "",
  issuerTaxConditionCode: "",
  validationStatus: "not_configured",
  hasCertificate: false,
  hasPrivateKey: false,
  certificate: {},
  lastValidatedAt: null,
  lastSuccessfulSyncAt: null,
  lastError: "",
};

function digits(value, max = 20) {
  return String(value || "").replace(/\D+/g, "").slice(0, max);
}

function formatDate(value, fallback = "—") {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(date);
}

function money(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currency || "ARS",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function entityId(value) {
  return String(value?._id || value?.id || value || "");
}

function statusMeta(integration) {
  if (integration.connected) return { label: "ARCA conectado", color: "success", icon: <CheckCircleRoundedIcon /> };
  if (integration.connectionStatus === "error") return { label: "Conexión con error", color: "error", icon: <VerifiedUserRoundedIcon /> };
  if (integration.validationStatus === "valid") return { label: "Falta probar ARCA", color: "warning", icon: <VerifiedUserRoundedIcon /> };
  if (integration.hasCertificate || integration.hasPrivateKey || integration.cuit) return { label: "Configuración incompleta", color: "warning", icon: <VerifiedUserRoundedIcon /> };
  return { label: "Sin configurar", color: "default", icon: <VerifiedUserRoundedIcon /> };
}

function documentStatusMeta(status) {
  switch (status) {
    case "authorized": return { label: "Autorizada", color: "success" };
    case "issuing": return { label: "Emitiendo", color: "info" };
    case "reconciling": return { label: "Conciliando", color: "warning" };
    case "retry_pending": return { label: "Reintento pendiente", color: "warning" };
    case "rejected": return { label: "Rechazada", color: "error" };
    case "cancelled": return { label: "Cancelada", color: "default" };
    default: return { label: "Lista para emitir", color: "default" };
  }
}

function expectedVoucher(issuerCode, recipientCode) {
  if (Number(issuerCode) === 1) return A_RECIPIENT_CODES.has(Number(recipientCode)) ? "Factura A" : "Factura B";
  if ([4, 6].includes(Number(issuerCode))) return "Factura C";
  return "Comprobante";
}

function compactPaperSx() {
  return {
    borderRadius: 2.5,
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "none",
    overflow: "hidden",
  };
}

export default function ProjectFiscalBillingPage() {
  const { projectId } = useParams();
  const [integration, setIntegration] = React.useState(emptyIntegration);
  const [form, setForm] = React.useState({
    enabled: true,
    environment: "prod",
    cuit: "",
    legalName: "",
    pointOfSale: "",
    issuerTaxConditionCode: "",
    certificatePem: "",
    privateKeyPem: "",
  });
  const [certificateFileName, setCertificateFileName] = React.useState("");
  const [privateKeyFileName, setPrivateKeyFileName] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [fiscalLoading, setFiscalLoading] = React.useState(false);
  const [processingId, setProcessingId] = React.useState("");
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [documents, setDocuments] = React.useState([]);
  const [candidates, setCandidates] = React.useState([]);
  const [invoiceCandidate, setInvoiceCandidate] = React.useState(null);
  const [recipient, setRecipient] = React.useState({ name: "", documentNumber: "", taxConditionCode: 5, address: "" });

  const applyIntegration = React.useCallback((nextIntegration = {}) => {
    const next = { ...emptyIntegration, ...nextIntegration };
    setIntegration(next);
    setForm({
      enabled: next.enabled !== false,
      environment: next.environment || "prod",
      cuit: next.cuit || "",
      legalName: next.legalName || "",
      pointOfSale: next.pointOfSale ? String(next.pointOfSale) : "",
      issuerTaxConditionCode: next.issuerTaxConditionCode ? String(next.issuerTaxConditionCode) : "",
      certificatePem: "",
      privateKeyPem: "",
    });
    setCertificateFileName("");
    setPrivateKeyFileName("");
  }, []);

  const loadFiscal = React.useCallback(async () => {
    setFiscalLoading(true);
    try {
      const [docsData, candidatesData] = await Promise.all([
        fiscalApi.listDocuments(projectId, { pageSize: 30 }),
        fiscalApi.listCandidates(projectId, { limit: 50 }),
      ]);
      setDocuments(docsData.documents || []);
      setCandidates(candidatesData.candidates || []);
    } catch (err) {
      setError(err.message || "No se pudo cargar el estado de facturación.");
    } finally {
      setFiscalLoading(false);
    }
  }, [projectId]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await integrationsApi.getArca(projectId);
      applyIntegration(data.integration || emptyIntegration);
      await loadFiscal();
    } catch (err) {
      setError(err.message || "No se pudo cargar la facturación ARCA.");
    } finally {
      setLoading(false);
    }
  }, [projectId, applyIntegration, loadFiscal]);

  React.useEffect(() => { load(); }, [load]);

  const dirty = React.useMemo(() => {
    if (loading) return false;
    return Boolean(
      form.enabled !== (integration.enabled !== false)
      || form.environment !== (integration.environment || "prod")
      || form.cuit !== (integration.cuit || "")
      || form.legalName !== (integration.legalName || "")
      || String(form.pointOfSale || "") !== String(integration.pointOfSale || "")
      || String(form.issuerTaxConditionCode || "") !== String(integration.issuerTaxConditionCode || "")
      || form.certificatePem
      || form.privateKeyPem
    );
  }, [loading, form, integration]);
  useProjectSettingsDirty("arca-billing", dirty);

  const change = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const readCredentialFile = async (event, field, setName) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      change(field, await file.text());
      setName(file.name || "Archivo cargado");
      setError("");
    } catch {
      setError("No se pudo leer el archivo seleccionado.");
    }
  };

  const save = async (event) => {
    event?.preventDefault?.();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        enabled: form.enabled,
        environment: form.environment,
        cuit: form.cuit,
        legalName: form.legalName.trim(),
        pointOfSale: Number(form.pointOfSale || 0),
        issuerTaxConditionCode: Number(form.issuerTaxConditionCode || 0),
      };
      if (form.certificatePem.trim()) payload.certificatePem = form.certificatePem;
      if (form.privateKeyPem.trim()) payload.privateKeyPem = form.privateKeyPem;
      const data = await integrationsApi.saveArca(projectId, payload);
      applyIntegration(data.integration || emptyIntegration);
      setMessage(data.message || "Configuración fiscal guardada.");
    } catch (err) {
      if (err?.data?.integration) applyIntegration(err.data.integration);
      setError(err.message || "No se pudo guardar la configuración fiscal.");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setError("");
    setMessage("");
    try {
      const data = await integrationsApi.testArca(projectId);
      applyIntegration(data.integration || emptyIntegration);
      setMessage(data.message || "ARCA conectado correctamente.");
      await loadFiscal();
    } catch (err) {
      if (err?.data?.integration) applyIntegration(err.data.integration);
      setError(err.message || "No se pudo conectar con ARCA.");
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar las credenciales fiscales de este proyecto? Los comprobantes ya emitidos no se eliminan.")) return;
    setDeleting(true);
    setError("");
    try {
      const data = await integrationsApi.deleteArca(projectId);
      applyIntegration(data.integration || emptyIntegration);
      setMessage(data.message || "Credenciales eliminadas.");
    } catch (err) {
      setError(err.message || "No se pudieron eliminar las credenciales.");
    } finally {
      setDeleting(false);
    }
  };

  const openInvoice = (candidate) => {
    const movement = candidate?.movement || {};
    setInvoiceCandidate(candidate);
    setRecipient({
      name: movement.party?.name || "Consumidor final",
      documentNumber: digits(movement.party?.document || "", 11),
      taxConditionCode: 5,
      address: "",
    });
    setError("");
  };

  const closeInvoice = () => {
    if (processingId) return;
    setInvoiceCandidate(null);
  };

  const createAndIssue = async () => {
    const movement = invoiceCandidate?.movement;
    const movementId = entityId(movement);
    if (!movementId) return;
    setProcessingId(movementId);
    setError("");
    setMessage("");
    try {
      const created = await fiscalApi.createFromMovement(projectId, movementId, {
        name: recipient.name,
        documentNumber: recipient.documentNumber,
        taxConditionCode: Number(recipient.taxConditionCode),
        address: recipient.address,
      });
      const doc = created.document;
      const issued = await fiscalApi.issue(projectId, entityId(doc));
      const fiscalDocument = issued.document || doc;
      if (fiscalDocument.status === "authorized") {
        setMessage(`${VOUCHER_LABELS[fiscalDocument.voucherType] || "Factura"} ${fiscalDocument.displayNumber} autorizada. CAE ${fiscalDocument.cae}.`);
      } else {
        setMessage("El comprobante quedó guardado y WebProfit continuará su conciliación segura con ARCA.");
      }
      setInvoiceCandidate(null);
      await loadFiscal();
    } catch (err) {
      setError(err.message || "No se pudo emitir el comprobante. Si ARCA quedó incierto, WebProfit lo conciliará antes de reintentar.");
      setInvoiceCandidate(null);
      await loadFiscal();
    } finally {
      setProcessingId("");
    }
  };

  const retryDocument = async (document) => {
    const id = entityId(document);
    if (!id) return;
    setProcessingId(id);
    setError("");
    try {
      await fiscalApi.retry(projectId, id);
      await loadFiscal();
    } catch (err) {
      setError(err.message || "No se pudo reintentar. El comprobante conserva su estado para conciliación.");
      await loadFiscal();
    } finally {
      setProcessingId("");
    }
  };

  if (loading) return <DashboardLoadingState label="Cargando facturación..." />;

  const status = statusMeta(integration);
  const hasStoredCredentials = integration.hasCertificate || integration.hasPrivateKey;
  const pendingCandidates = candidates.filter((row) => !row.fiscalDocument);

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Facturación</Typography>
          <Typography variant="body2" color="text.secondary">ARCA + ventas ERP, con emisión idempotente y conciliación segura.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip icon={status.icon} label={status.label} color={status.color} variant="outlined" sx={{ fontWeight: 900 }} />
          <Tooltip title="Actualizar">
            <span><IconButton size="small" onClick={load} disabled={fiscalLoading}><RefreshRoundedIcon fontSize="small" /></IconButton></span>
          </Tooltip>
        </Stack>
      </Stack>

      {form.environment === "homo" ? <Alert severity="warning">Estás en homologación. Los comprobantes de este ambiente no son facturas productivas.</Alert> : null}
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      {message ? <Alert severity="success" onClose={() => setMessage("")}>{message}</Alert> : null}

      <Paper variant="outlined" sx={compactPaperSx()} component="form" onSubmit={save}>
        <Stack direction={{ xs: "column", lg: "row" }} divider={<Divider orientation="vertical" flexItem />}>
          <Stack spacing={1.2} sx={{ p: 1.5, flex: 1.15 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography sx={{ fontWeight: 950 }}>Emisor fiscal</Typography>
                <Typography variant="caption" color="text.secondary">Identidad y punto de venta habilitado para WSFEv1.</Typography>
              </Box>
              <Switch checked={form.enabled} onChange={(event) => change("enabled", event.target.checked)} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField size="small" label="CUIT" value={form.cuit} onChange={(event) => change("cuit", digits(event.target.value, 11))} required fullWidth />
              <TextField size="small" label="Razón social" value={form.legalName} onChange={(event) => change("legalName", event.target.value)} fullWidth />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField size="small" label="Punto de venta" value={form.pointOfSale} onChange={(event) => change("pointOfSale", digits(event.target.value, 5))} required fullWidth />
              <TextField size="small" select label="Condición fiscal" value={form.issuerTaxConditionCode} onChange={(event) => change("issuerTaxConditionCode", event.target.value)} required fullWidth>
                {ISSUER_CONDITIONS.map((option) => <MenuItem key={option.value} value={String(option.value)}>{option.label}</MenuItem>)}
              </TextField>
              <TextField size="small" select label="Ambiente" value={form.environment} onChange={(event) => change("environment", event.target.value)} fullWidth>
                <MenuItem value="prod">Producción</MenuItem>
                <MenuItem value="homo">Homologación</MenuItem>
              </TextField>
            </Stack>
          </Stack>

          <Stack spacing={1.2} sx={{ p: 1.5, flex: .85 }}>
            <Box>
              <Typography sx={{ fontWeight: 950 }}>Certificado</Typography>
              <Typography variant="caption" color="text.secondary">Certificado y clave se guardan cifrados; la clave privada no vuelve al navegador.</Typography>
            </Box>
            <Button component="label" size="small" variant="outlined" startIcon={<UploadFileRoundedIcon />} sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 850 }}>
              {certificateFileName || (integration.hasCertificate ? "Reemplazar certificado" : "Cargar certificado .crt / .pem")}
              <input hidden type="file" accept=".crt,.cer,.pem,text/plain" onChange={(event) => readCredentialFile(event, "certificatePem", setCertificateFileName)} />
            </Button>
            <Button component="label" size="small" variant="outlined" startIcon={<KeyRoundedIcon />} sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 850 }}>
              {privateKeyFileName || (integration.hasPrivateKey ? "Reemplazar clave privada" : "Cargar clave privada .key / .pem")}
              <input hidden type="file" accept=".key,.pem,text/plain" onChange={(event) => readCredentialFile(event, "privateKeyPem", setPrivateKeyFileName)} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              {integration.certificate?.validTo ? `Certificado vigente hasta ${formatDate(integration.certificate.validTo)}.` : "Todavía no hay certificado validado."}
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ p: 1.2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button type="submit" size="small" variant="contained" startIcon={<SaveRoundedIcon />} disabled={saving || testing || deleting} sx={{ textTransform: "none", fontWeight: 900 }}>
              {saving ? "Guardando..." : "Guardar configuración"}
            </Button>
            {hasStoredCredentials ? (
              <Button type="button" size="small" variant={integration.connected ? "outlined" : "contained"} startIcon={<VerifiedUserRoundedIcon />} onClick={testConnection} disabled={saving || testing || deleting || dirty} sx={{ textTransform: "none", fontWeight: 900 }}>
                {testing ? "Conectando con ARCA..." : integration.connected ? "Probar conexión" : "Conectar y probar ARCA"}
              </Button>
            ) : null}
          </Stack>
          {hasStoredCredentials ? (
            <Button type="button" size="small" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={remove} disabled={saving || testing || deleting} sx={{ textTransform: "none", fontWeight: 850 }}>
              {deleting ? "Eliminando..." : "Eliminar credenciales"}
            </Button>
          ) : null}
        </Stack>
      </Paper>

      {!integration.connected ? (
        <Alert severity="info">
          Guardá las credenciales y probá la conexión real. WebProfit no habilita emisión hasta que WSAA, WSFEv1 y el punto de venta respondan correctamente.
        </Alert>
      ) : (
        <>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
            <Paper variant="outlined" sx={{ ...compactPaperSx(), p: 1.35, flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Ventas listas para facturar</Typography>
              <Typography variant="h5" sx={{ fontWeight: 950, mt: .2 }}>{pendingCandidates.length}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ ...compactPaperSx(), p: 1.35, flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Última conexión correcta</Typography>
              <Typography sx={{ fontWeight: 900, mt: .45 }}>{formatDate(integration.lastSuccessfulSyncAt)}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ ...compactPaperSx(), p: 1.35, flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Punto de venta</Typography>
              <Typography sx={{ fontWeight: 900, mt: .45 }}>#{String(integration.pointOfSale || "—").padStart(5, "0")}</Typography>
            </Paper>
          </Stack>

          <Paper variant="outlined" sx={compactPaperSx()}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" sx={{ px: 1.5, py: 1.15 }} spacing={.7}>
              <Box>
                <Typography sx={{ fontWeight: 950 }}>Ventas pendientes</Typography>
                <Typography variant="caption" color="text.secondary">Una venta ERP confirmada se convierte en un único comprobante fiscal.</Typography>
              </Box>
            </Stack>
            <Divider />
            <TableContainer sx={{ maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Venta</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right" width={120}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingCandidates.length ? pendingCandidates.map((candidate) => {
                    const movement = candidate.movement || {};
                    const id = entityId(movement);
                    return (
                      <TableRow key={id} hover>
                        <TableCell sx={{ fontWeight: 850 }}>{movement.movementNumber || "Venta"}</TableCell>
                        <TableCell>{movement.party?.name || "Consumidor final"}</TableCell>
                        <TableCell>{formatDate(movement.documentDate || movement.occurredAt)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 850 }}>{money(movement.total, movement.currency)}</TableCell>
                        <TableCell align="right">
                          <Button size="small" startIcon={<ReceiptLongRoundedIcon />} onClick={() => openInvoice(candidate)} disabled={Boolean(processingId)} sx={{ textTransform: "none", fontWeight: 900 }}>Facturar</Button>
                        </TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>No hay ventas confirmadas pendientes de facturar.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={compactPaperSx()}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1.15 }}>
              <Box>
                <Typography sx={{ fontWeight: 950 }}>Comprobantes fiscales</Typography>
                <Typography variant="caption" color="text.secondary">CAE, reintentos y conciliación quedan auditados acá.</Typography>
              </Box>
              {fiscalLoading ? <Typography variant="caption" color="text.secondary">Actualizando...</Typography> : null}
            </Stack>
            <Divider />
            <TableContainer sx={{ maxHeight: 330 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Receptor</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>CAE</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right" width={120}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.length ? documents.map((document) => {
                    const id = entityId(document);
                    const meta = documentStatusMeta(document.status);
                    const canRetry = ["rejected", "retry_pending", "reconciling"].includes(document.status);
                    return (
                      <TableRow key={id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>{VOUCHER_LABELS[document.voucherType] || `Tipo ${document.voucherType}`}</Typography>
                          <Typography variant="caption" color="text.secondary">{document.displayNumber || document.sourceNumber || "Sin número"}</Typography>
                        </TableCell>
                        <TableCell>{document.recipient?.name || "Consumidor final"}</TableCell>
                        <TableCell><Chip size="small" label={meta.label} color={meta.color} variant="outlined" /></TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontWeight: document.cae ? 800 : 400 }}>{document.cae || "—"}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 850 }}>{money(document.amounts?.total, document.currency)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" justifyContent="flex-end" spacing={.3}>
                            {document.qr?.url ? (
                              <Tooltip title="Abrir QR fiscal">
                                <IconButton size="small" component="a" href={document.qr.url} target="_blank" rel="noreferrer"><LaunchRoundedIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            ) : null}
                            {canRetry ? (
                              <Tooltip title="Reintentar / conciliar">
                                <span><IconButton size="small" onClick={() => retryDocument(document)} disabled={Boolean(processingId)}><ReplayRoundedIcon fontSize="small" /></IconButton></span>
                              </Tooltip>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>Todavía no hay comprobantes emitidos desde WebProfit.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      <Dialog open={Boolean(invoiceCandidate)} onClose={closeInvoice} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: .6, fontWeight: 950 }}>Emitir comprobante</DialogTitle>
        <DialogContent>
          <Stack spacing={1.2} sx={{ pt: .8 }}>
            <Alert severity="info" icon={false}>
              Se emitirá <strong>{expectedVoucher(form.issuerTaxConditionCode, recipient.taxConditionCode)}</strong> por {money(invoiceCandidate?.movement?.total, invoiceCandidate?.movement?.currency)}. Una vez autorizada por ARCA, la venta queda vinculada al CAE y no se vuelve a emitir.
            </Alert>
            <TextField size="small" label="Nombre / razón social" value={recipient.name} onChange={(event) => setRecipient((current) => ({ ...current, name: event.target.value }))} fullWidth />
            <TextField size="small" select label="Condición IVA del receptor" value={recipient.taxConditionCode} onChange={(event) => setRecipient((current) => ({ ...current, taxConditionCode: Number(event.target.value) }))} fullWidth>
              {RECIPIENT_CONDITIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
            <TextField size="small" label="CUIT / CUIL / DNI" value={recipient.documentNumber} onChange={(event) => setRecipient((current) => ({ ...current, documentNumber: digits(event.target.value, 11) }))} helperText={A_RECIPIENT_CODES.has(Number(recipient.taxConditionCode)) ? "Para Factura A se requiere CUIT/CUIL de 11 dígitos." : "Para Consumidor Final puede quedar vacío cuando corresponda."} fullWidth />
            <TextField size="small" label="Domicilio fiscal (opcional)" value={recipient.address} onChange={(event) => setRecipient((current) => ({ ...current, address: event.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeInvoice} disabled={Boolean(processingId)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" onClick={createAndIssue} disabled={Boolean(processingId)} startIcon={<ReceiptLongRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>
            {processingId ? "Emitiendo..." : "Emitir en ARCA"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
